import { useEffect, useState } from "react";

import defaultTrxImg from "@/assets/images/default_transaction.png";
import "@/assets/scss/modern-modal.css";
import { Badge } from "@/components/Common/Badge";
import { DateTimeLabel } from "@/components/Common/DateTimeLabel";
import { formatDate } from "@/helpers/date_helper";

export default function TransactionDetailsModal({ title, show, fullscreen: initialFullscreen = false, data, tabs = [], buttons = [], onCloseClick, tabRefreshKey = 0 }) {
  const [fullscreen, setFullscreen] = useState(initialFullscreen);
  const [activeTab, setActiveTab] = useState(tabs[0]?.key);

  useEffect(() => {
    if (show) {
      setActiveTab(tabs[0]?.key);
      setFullscreen(initialFullscreen);
    }
  }, [show]);

  const handleClose = () => {
    onCloseClick?.();
  };

  if (!show) return null;

  const activeTabDef = tabs.find((t) => t.key === activeTab);
  const TabComponent = activeTabDef?.component ?? null;

  return (
    <>
      <div className={`emd-overlay${fullscreen ? " emd-fs" : ""}`}>
        <div className={`emd-box${fullscreen ? " emd-fullscreen" : ""}`}>
          {/* ══════ HEADER ══════ */}
          <div className="emd-hdr">
            <div style={{ display: "flex", alignItems: "flex-start", gap: 16, marginBottom: 16 }}>
              <div className="emd-avatar-wrap">
                <img src={defaultTrxImg} style={{ height: "55px" }} />
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 4 }}>
                  <>
                    <span style={{ fontSize: 20, fontWeight: 800, color: "#fff" }}>{title}</span>
                  </>
                </div>
                <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
                  {[
                    { icon: "ri-edit-line", val: data.submittedByName },
                    { icon: "ri-calendar-line", val: formatDate(data.dateSubmitted) },
                  ].map((item, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 4, color: "rgba(255,255,255,0.58)", fontSize: 12 }}>
                      <i className={item.icon} />
                      {item.val}
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
                {buttons
                  .filter((btn) => btn.show)
                  .map((btn, i) => (
                    <button key={i} className={`emd-btn emd-btn-sm ${btn.className ?? ""}`} onClick={btn.onClick}>
                      {btn.icon && <i className={btn.icon} />} {btn.label}
                    </button>
                  ))}

                {/* Fullscreen toggle */}
                <button className="emd-icon-btn" onClick={() => setFullscreen((f) => !f)} title={fullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}>
                  <i className={fullscreen ? "ri-fullscreen-exit-line" : "ri-fullscreen-line"} />
                </button>
                {/* Close */}
                <button className="emd-icon-btn emd-close" onClick={handleClose} title="Close">
                  <i className="ri-close-line" style={{ fontSize: 17 }} />
                </button>
              </div>
            </div>

            {/* Stats strip */}
            <div style={{ display: "flex", gap: 28, flexWrap: "wrap" }}>
              {[
                { label: "Reference No.", val: data.referenceNo },
                {
                  label: "Status", val: <Badge value={data?.statusName} color={data?.statusColor} />
                },
                { label: `Date ${data?.statusName}`, val: formatDate(data.statusDate) },
              ].map((s, i) => (
                <div key={i} style={{ paddingBottom: 12 }}>
                  <div style={{ fontSize: 10, color: "rgba(255,255,255,0.42)", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 2 }}>
                    {s.label}
                  </div>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: "#fff" }}>{s.val}</div>
                </div>
              ))}
            </div>

            {/* Tabs */}
            <div className="emd-tab-bar">
              {tabs.map((t) => (
                <button key={t.key} className={`emd-tab${activeTab === t.key ? " active" : ""}`} onClick={() => setActiveTab(t.key)}>
                  <i className={t.icon} />
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* ══════ TAB CONTENT ══════ */}
          <div className="emd-body" style={{ minHeight: 620 }}>
            {TabComponent ? <TabComponent key={`${activeTab}-${tabRefreshKey}`} data={data} /> : null}
          </div>

          {/* ══════ FOOTER ══════ */}
          <div className="emd-ftr">
            <span style={{ fontSize: 12 }}>
              Last updated: <DateTimeLabel value={data.modifiedDate} />
            </span>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="emd-btn emd-btn-muted">
                <i className="ri-printer-line" /> Print
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
